import ExcelJS from "exceljs";

import { PalletListRow } from "@/utils/palletList";

/**
 * Палетный лист в XLSX (ExcelJS) — по утверждённому образцу «Шеф_Т_форма.xlsx».
 *
 * Лист — сетка из 20 узких колонок (ширина 5.15), печатная область A:T:
 *   строки 1–4   A1:T4  — полное наименование продукта (Arial bold,
 *                кегль ступенчато уменьшается с длиной, рамка, перенос)
 *   строки 5–8   A5:J8  — ТМ «"ОСТБЕРГ"» (26 bold, перенос) |
 *                K5:T8  — тара «5,6» (48 bold)
 *   строка 10    A10:J10 — полное наименование «... (тара 5,6кг)» (9pt) |
 *                K10:N10 «ФИО:» (12 bold) | O10:T10 — пусто, линия снизу
 *   строка 12    K12:N12 «Дата заполнения:» | O12:T12 — «07.10.2026 9:01:53»
 *   строки 13–14 K13:T14 — «Количество в листе соответствует фактическому
 *                          значению.» (10pt, перенос)
 *   строки 11–14 слева — картинка штрихкода (340×74), строка 15 — его
 *                значение текстом (9pt, по центру); без штрихкода —
 *                предупреждение вместо картинки
 *   строка 16    шапка таблицы: «Дата» (A:H) | «№ п.» (I:N) | «шт» (O:T) —
 *                28pt, по центру, тонкая линия снизу (без заливки)
 *   строки 17–27 — 11 строк таблицы (30pt) с линией снизу; первая
 *                предзаполнена из факта, остальные — под ручное заполнение
 *
 * Лист смены: по листу на группу SKU — продукт с одной тарой (имя листа —
 * короткое название, повторы уникализируются), партии группы заполняют
 * строки таблицы.
 */

/** Данные одного блока палетного листа (группа SKU: продукт + тара) */
export interface PalletListXlsxBlock {
  /** № первой партии группы (fallback имени листа) */
  number: string;
  /** все номера партий группы — для имени листа при группировке за смену */
  numbers?: string[];
  /** Короткое название («Шеф Т») — только для имени листа и имени файла;
   *  шапку печатает product_name; пусто — имя листа от номера партии */
  short_name?: string;
  product_name: string;
  trademark: string;
  /** тара, кг («5,6») */
  container: string;
  /** значение штрихкода; пустая строка — предупреждение вместо картинки */
  barcode: string;
  /** PNG штрихкода в data-URL (canvas.toDataURL) — вставляется картинкой */
  barcodeImage?: string | null;
  /** фактические размеры PNG, px — чтобы не растягивать картинку в зоне */
  barcodeImageSize?: { width: number; height: number } | null;
  /** предупреждение о фолбэке EAN-13 → CODE128 (ean13FallbackWarning):
   *  печатается строкой под картинкой штрихкода */
  barcodeWarning?: string | null;
  rows: PalletListRow[];
}

/** Общие данные листа */
export interface PalletListXlsxDoc {
  blocks: PalletListXlsxBlock[];
  /** ФИО — пустая строка, заполняется на производстве */
  full_name: string;
  /** Дата заполнения «07.10.2026 9:01:53» */
  filled_at: string;
}

/** Ширина каждой колонки сетки образца */
const COL_WIDTH = 5.15;
const COL_COUNT = 20;

/** Максимальные габариты зоны картинки штрихкода (строки 11–14 слева), px */
const BARCODE_IMAGE_MAX_WIDTH = 340;
const BARCODE_IMAGE_MAX_HEIGHT = 110;
/** Пропорциональное уменьшение картинки от рендера и сдвиг вправо (в долях
 *  ширины колонки A) — по вычитке печатной формы */
const BARCODE_IMAGE_SCALE = 0.85;
const BARCODE_IMAGE_LEFT_COL = 0.75;

const MEDIUM_BORDER = {
  top: { style: "medium" as const, color: { argb: "FF000000" } },
  left: { style: "medium" as const, color: { argb: "FF000000" } },
  bottom: { style: "medium" as const, color: { argb: "FF000000" } },
  right: { style: "medium" as const, color: { argb: "FF000000" } },
};

const THIN_BOTTOM = {
  bottom: { style: "thin" as const, color: { argb: "FF000000" } },
};

/** Кегль шапки по длине полного наименования: короткое имя печатается 48,
 *  длинное — уменьшается ступенями, чтобы вместиться в зону 4 строк.
 *  Пороги подобраны под сетку 20 колонок × 5.15 (≈27 симв. в строке при 48). */
export function headerFontSize(productName?: string | null): number {
  const length = (productName || "").length;
  if (length <= 16) return 48;
  if (length <= 32) return 36;
  return 28;
}

/** Дата заполнения как в образце: «07.10.2026 9:01:53» (без ведущего нуля часа) */
export function formatFilledAtXlsx(date: Date = new Date()): string {
  const pad = (v: number) => String(v).padStart(2, "0");
  const h = date.getHours();
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${h}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** data-URL → base64-часть для ExcelJS addImage */
function dataUrlBase64(dataUrl: string): { base64: string } | null {
  const match = /^data:image\/png;base64,(.+)$/.exec(dataUrl.trim());
  return match ? { base64: match[1] } : null;
}

/**
 * Рамка по периметру merged-зоны: ExcelJS рисует границы отдельных ячеек,
 * поэтому border нужен каждой ячейке диапазона, а не только мастер-ячейке.
 */
function applyZoneBorder(
  sheet: ExcelJS.Worksheet,
  fromRow: number, fromCol: number, toRow: number, toCol: number,
) {
  for (let r = fromRow; r <= toRow; r++) {
    for (let c = fromCol; c <= toCol; c++) {
      sheet.getRow(r).getCell(c).border = MEDIUM_BORDER;
    }
  }
}

/** Лист одного блока по образцу (строки 1–27) */
function buildBlockSheet(
  wb: ExcelJS.Workbook,
  block: PalletListXlsxBlock,
  doc: PalletListXlsxDoc,
  sheetName: string,
): ExcelJS.Worksheet {
  const sheet = wb.addWorksheet(sheetName, {
    pageSetup: { fitToPage: true, orientation: "portrait" },
  });
  sheet.pageSetup.margins = {
    left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0, footer: 0,
  };
  sheet.headerFooter = { oddHeader: "", oddFooter: "" };
  for (let c = 1; c <= COL_COUNT; c++) sheet.getColumn(c).width = COL_WIDTH;

  // Строки 1–4: полное наименование продукта (bold, рамка medium, перенос —
  // длинные названия разбиваются на строки), шрифт ступенчато уменьшается,
  // чтобы полное имя влезло в зону 4 строк
  sheet.mergeCells(1, 1, 4, COL_COUNT);
  const title = sheet.getRow(1).getCell(1);
  title.value = block.product_name || "";
  title.font = { name: "Arial", size: headerFontSize(block.product_name), bold: true };
  title.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  applyZoneBorder(sheet, 1, 1, 4, COL_COUNT);
  for (let r = 1; r <= 4; r++) sheet.getRow(r).height = 40;

  // Строки 5–8: ТМ слева (26 bold, перенос, кавычки) и тара справа (48 bold)
  sheet.mergeCells(5, 1, 8, 10);
  const trademark = sheet.getRow(5).getCell(1);
  trademark.value = block.trademark ? `ТМ\n"${block.trademark}"` : "";
  trademark.font = { name: "Arial", size: 26, bold: true };
  trademark.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  applyZoneBorder(sheet, 5, 1, 8, 10);

  sheet.mergeCells(5, 11, 8, COL_COUNT);
  const tare = sheet.getRow(5).getCell(11);
  tare.value = block.container || "";
  tare.font = { name: "Arial", size: 48, bold: true };
  tare.alignment = { horizontal: "center", vertical: "middle" };
  applyZoneBorder(sheet, 5, 11, 8, COL_COUNT);
  for (let r = 5; r <= 8; r++) sheet.getRow(r).height = 28;

  // Строка 10: полное наименование | ФИО | значение ФИО (линия снизу)
  sheet.mergeCells(10, 1, 10, 10);
  const fullNameTitle = sheet.getRow(10).getCell(1);
  fullNameTitle.value = `${block.product_name || ""} (тара ${block.container || ""}кг)`;
  fullNameTitle.font = { name: "Arial", size: 9 };
  fullNameTitle.alignment = { horizontal: "left", vertical: "bottom" };

  sheet.mergeCells(10, 11, 10, 14);
  const fioLabel = sheet.getRow(10).getCell(11);
  fioLabel.value = "ФИО:";
  fioLabel.font = { name: "Arial", size: 12, bold: true };
  fioLabel.alignment = { horizontal: "left", vertical: "middle" };

  sheet.mergeCells(10, 15, 10, COL_COUNT);
  const fioValue = sheet.getRow(10).getCell(15);
  fioValue.value = doc.full_name || "";
  fioValue.font = { name: "Arial", size: 12 };
  fioValue.alignment = { horizontal: "center", vertical: "middle" };
  fioValue.border = THIN_BOTTOM;

  // Строка 12: дата заполнения
  sheet.mergeCells(12, 11, 12, 14);
  const dateLabel = sheet.getRow(12).getCell(11);
  dateLabel.value = "Дата заполнения:";
  dateLabel.font = { name: "Arial", size: 11, bold: true };
  dateLabel.alignment = { horizontal: "left", vertical: "middle" };

  sheet.mergeCells(12, 15, 12, COL_COUNT);
  const dateValue = sheet.getRow(12).getCell(15);
  dateValue.value = doc.filled_at || "";
  dateValue.font = { name: "Arial", size: 11 };
  dateValue.alignment = { horizontal: "left", vertical: "middle" };

  // Строки 13–14: итоговая строка
  sheet.mergeCells(13, 11, 14, COL_COUNT);
  const confirm = sheet.getRow(13).getCell(11);
  confirm.value = "Количество в листе соответствует фактическому значению.";
  confirm.font = { name: "Arial", size: 10 };
  confirm.alignment = { horizontal: "left", vertical: "middle", wrapText: true };

  // Штрихкод: картинка в левой половине (строки 11–14) и значение в строке 15
  const barcodeImage = block.barcodeImage ? dataUrlBase64(block.barcodeImage) : null;
  if (barcodeImage) {
    // размеры — по факту рендера (пропорции JsBarcode), с вписыванием в зону
    const src = block.barcodeImageSize || { width: BARCODE_IMAGE_MAX_WIDTH, height: 74 };
    const scale = Math.min(
      BARCODE_IMAGE_MAX_WIDTH / src.width,
      BARCODE_IMAGE_MAX_HEIGHT / src.height,
      1,
    ) * BARCODE_IMAGE_SCALE;
    const imageId = wb.addImage({ base64: barcodeImage.base64, extension: "png" });
    sheet.addImage(imageId, {
      tl: { col: BARCODE_IMAGE_LEFT_COL, row: 10.04 },
      ext: { width: Math.round(src.width * scale), height: Math.round(src.height * scale) },
    });
  } else {
    sheet.mergeCells(11, 1, 14, 10);
    const warn = sheet.getRow(11).getCell(1);
    warn.value = block.barcode
      ? "Штрихкод не удалось отрисовать — проверьте значение"
      : "Штрихкод не заполнен";
    warn.font = { name: "Arial", size: 10, italic: true };
    warn.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  }

  // строка 15 — отступ перед таблицей; значение штрихкода печатается
  // в самой картинке (подпись рендера), отдельным текстом не дублируем;
  // при фолбэке EAN-13 → CODE128 под картинкой печатается предупреждение
  if (block.barcodeWarning) {
    sheet.mergeCells(15, 1, 15, 10);
    const warn = sheet.getRow(15).getCell(1);
    warn.value = block.barcodeWarning;
    warn.font = { name: "Arial", size: 8, italic: true };
    warn.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
    sheet.getRow(15).height = 20;
  } else {
    sheet.getRow(15).height = 14;
  }

  // Строка 16: шапка таблицы «Дата | № п. | шт» — крупная, с линией снизу
  const TABLE_ZONES: Array<{ from: number; to: number; label: string }> = [
    { from: 1, to: 8, label: "Дата" },
    { from: 9, to: 14, label: "№ п." },
    { from: 15, to: COL_COUNT, label: "шт" },
  ];
  for (const zone of TABLE_ZONES) {
    if (zone.to > zone.from) sheet.mergeCells(16, zone.from, 16, zone.to);
    const cell = sheet.getRow(16).getCell(zone.from);
    cell.value = zone.label;
    cell.font = { name: "Arial", size: 28 };
    cell.alignment = { horizontal: "center", vertical: "middle" };
  }
  for (let c = 1; c <= COL_COUNT; c++) {
    const cell = sheet.getRow(16).getCell(c);
    cell.border = THIN_BOTTOM;
    if (c === 1 || c === 9 || c === 15) {
      cell.font = { name: "Arial", size: 28 };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    }
  }
  sheet.getRow(16).height = 36;

  // Строки 17–27: строки таблицы, первая — из факта, остальные пустые;
  // зоны «Дата | № п. | шт» объединяются построчно (A:H, I:N, O:T) —
  // иначе значение в узкой ячейке (ширина 5.15) обрезается
  const TABLE_ROWS = 11;
  const rows = (block.rows && block.rows.length ? block.rows : [{ date: "", batch_number: "", quantity: "" }]);
  const TABLE_DATA_ZONES: Array<{ from: number; to: number; value: (row: PalletListRow) => string }> = [
    { from: 1, to: 8, value: (row) => row.date || "" },
    { from: 9, to: 14, value: (row) => row.batch_number || "" },
    { from: 15, to: COL_COUNT, value: (row) => row.quantity || "" },
  ];
  for (let i = 0; i < TABLE_ROWS; i++) {
    const r = 17 + i;
    sheet.getRow(r).height = 30;
    const row = rows[i] || { date: "", batch_number: "", quantity: "" };
    for (const zone of TABLE_DATA_ZONES) {
      if (zone.to > zone.from) sheet.mergeCells(r, zone.from, r, zone.to);
      const cell = sheet.getRow(r).getCell(zone.from);
      cell.value = zone.value(row);
      cell.font = { name: "Arial", size: 20 };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    }
    for (let c = 1; c <= COL_COUNT; c++) {
      sheet.getRow(r).getCell(c).border = THIN_BOTTOM;
    }
  }

  return sheet;
}

/** Уникальное имя листа: номер партии, повторы — с «(2)», «(3)» */
function uniqueSheetName(base: string, used: Set<string>): string {
  // лимит имени листа Excel — 31 символ; входящее имя уже собрано с тарой
  const clean = (base || "Партия").replace(/[\\/?*[\]:]/g, "").slice(0, 31) || "Партия";
  let name = clean;
  let copy = 2;
  while (used.has(name)) name = `${clean} (${copy++})`;
  used.add(name);
  return name;
}

/** Собирает workbook палетного листа (синхронно) — для компонентов и тестов */
export function createPalletListWorkbook(doc: PalletListXlsxDoc): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  const used = new Set<string>();
  doc.blocks.forEach((block) => {
    // имя листа — короткое название с тарой («Шеф М 5,6»): у одного продукта
    // с несколькими тарами группы сразу различимы; при длинном названии
    // урезается оно, тара остаётся (лимит имени листа Excel — 31 символ)
    const name = block.short_name || block.number;
    let sheetTitle = name;
    if (block.container) {
      const withTare = `${name} ${block.container}`;
      sheetTitle = withTare.length <= 31
        ? withTare
        : `${name.slice(0, Math.max(1, 30 - block.container.length))} ${block.container}`.slice(0, 31);
    }
    buildBlockSheet(wb, block, doc, uniqueSheetName(sheetTitle, used));
  });
  if (doc.blocks.length === 0) {
    // пустой документ — один пустой лист, чтобы файл открывался
    wb.addWorksheet("Палетный лист");
  }
  return wb;
}

/** Имя файла: палетный_лист_<название|партия>_<дата>.xlsx (без недопустимых символов) */
export function palletListXlsxFilename(doc: PalletListXlsxDoc): string {
  const first = doc.blocks[0];
  const subject =
    doc.blocks.length > 1
      ? "смена"
      : (first && (first.short_name || first.product_name) || "SKU");
  const safe = String(subject)
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 60);
  return `палетный_лист_${safe}_${formatFilledAtXlsx().split(" ")[0]}.xlsx`;
}

/** Собирает workbook и возвращает XLSX-буфер для скачивания */
export async function buildPalletListWorkbook(doc: PalletListXlsxDoc): Promise<ArrayBuffer> {
  try {
    const wb = createPalletListWorkbook(doc);
    return await wb.xlsx.writeBuffer();
  } catch (err) {
    console.error("[palletListXlsx] writeBuffer error", err);
    throw err;
  }
}
